import { useState } from 'react';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import { useRepository } from '@/Application/Providers/Repository';
import { useCart } from '@/Application/Selectors/cart/useCart';
import type { OptionSelected, TimeSlot } from '@/modules/cart/domain/Cart';
import { dateFormatterString } from '@/modules/perseo-billing/utils';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import type { GtmGroupsData } from '@/modules/shared/utils/gtmEvents/types';
import { ShippingEventInteractiveActions } from '@/modules/shared/utils/gtmEvents/types';

interface OptionSelectedFixed extends Omit<OptionSelected, 'timeSlot'> {
  timeSlots?: TimeSlot;
}

const useCollectShippingMethods = () => {
  const [loading, setLoading] = useState(false);
  const { cartShippingMethodsRepository, cart: cartRepository } =
    useRepository();
  const { data: cart } = useCart();
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;

  const getDeliveryGroupId = (lineItems: string[]) => {
    return deliveryGroups?.find((group) => {
      return group.products.every((lineItem) => lineItems.includes(lineItem));
    })?.id;
  };

  const getShopsByCommune = async (commune: string, skus: string[]) => {
    const data = await cartShippingMethodsRepository.getShops(commune, skus);
    return data.deliveryGroups[0];
  };

  const getCopecByCommune = async (commune: string, skus: string[]) => {
    const data = await cartShippingMethodsRepository.getCopec(commune, skus);
    return data.deliveryGroups[0];
  };

  const sendResultEvent = (
    title: string,
    retiro: string,
    dispatchGroup: string
  ) => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': title,
        'event-interactive-accion': ShippingEventInteractiveActions.Pickup,
        'event-interactive-retiro': retiro,
        'event-interactive-grupo_despacho': dispatchGroup
      }
    });
  };

  const updateCollectShippingMethod = async (
    deliveryCollectId: string,
    selectedStore: OptionSelectedFixed,
    skus: string[],
    gtmData?: GtmGroupsData
  ) => {
    setLoading(true);
    const deliveryGroupId = getDeliveryGroupId(skus);

    const retiro = `${selectedStore.collectNodeName} / ${dateFormatterString(selectedStore.shipDate)}`;
    const dispatchGroup = `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`;
    try {
      if (!deliveryGroupId) {
        throw new Error('No se encontró el grupo de entrega');
      }

      const payload = {
        id: deliveryCollectId,
        originNodeType: selectedStore.originNodeType,
        originNodeCode: selectedStore.originNodeCode,
        collectNodeCode: selectedStore.collectNodeCode
      };

      const res = await cartRepository.updateShippingOption(
        deliveryGroupId,
        payload
      );

      if (!res) {
        throw new Error('No se pudo cambiar la tienda');
      }

      setLoading(false);
      const title = 'Cambiaste la tienda con éxito';
      sendResultEvent(title, retiro, dispatchGroup);
      toast({ title });
      return res;
    } catch {
      const title = 'No pudimos cambiar la tienda';
      sendResultEvent(title, retiro, dispatchGroup);
      toast({
        status: 'error',
        title
      });
      setLoading(false);
      return undefined;
    }
  };

  return {
    getShopsByCommune,
    getCopecByCommune,
    updateCollectShippingMethod,
    loading
  };
};

export default useCollectShippingMethods;
